/**
 * Hover tooltip for wrapped / ellipsised chart axis tick labels.
 *
 * Category tick labels are laid out by G2 (see `chartAxisLabelTransforms.ts`): each label wraps onto
 * at most three lines inside its own tick space and is ellipsised beyond that, so a long category
 * name is legible but incomplete. This module restores the full name on hover.
 *
 * Why it is written against the G scene graph rather than the DOM: G2 renders with the canvas
 * renderer, so tick labels are `@antv/g` display objects, not DOM nodes — there is nothing to hang a
 * `title` attribute or an antd `<Tooltip>` on. Three facts about that scene graph make this work,
 * all verified against `@antv/component` and `@antv/g2`:
 *
 *  1. Every tick label shape is tagged with the class name `axis-label-item` (`classNamePrefix` adds
 *     a `g2-axis-label-item` alias), so a hovered shape can be resolved back to its label.
 *  2. The `ellipsis` / `wrap` transforms only ever set G *style* attributes (`wordWrap`,
 *     `wordWrapWidth`, `maxLines`, `textOverflow`) — the shape's `text` keeps the untruncated string,
 *     so the full label is readable off the shape G2 already rendered. Those styles are applied by
 *     `Xn`/`Un` to the label item when it is itself a text node, and otherwise to its single text
 *     child, which is why both are inspected here.
 *  3. G events bubble to `canvas.document.documentElement`, which survives re-layouts. Listening
 *     there rather than on each label shape means `forceFit`, scrolling and filtering — all of which
 *     rebuild the label shapes — cannot silently drop the handlers.
 *
 * Labels the axis is *not* wrapping (a value axis, or a category axis configured with full text) are
 * skipped, so hovering numeric ticks stays quiet.
 */

import { AXIS_LABEL_FONT_SIZE_PX, estimateTextWidthPx } from './axisLabelWidth';

/** Class name `@antv/component` puts on every axis tick label shape. */
export const AXIS_LABEL_ITEM_CLASS_NAME = 'axis-label-item';

/** Marker attribute on the tooltip element, for consumers and e2e. */
export const AXIS_LABEL_TOOLTIP_MARKER = 'data-asper-axis-label-tooltip';

/** Distance in px between the pointer and the tooltip box. */
const TOOLTIP_POINTER_OFFSET_PX = 14;

/** Kept clear between the tooltip and the viewport edge when the box has to be flipped. */
const TOOLTIP_VIEWPORT_MARGIN_PX = 8;

const TOOLTIP_STYLE: Readonly<Record<string, string>> = {
    position: 'fixed',
    zIndex: '1100',
    pointerEvents: 'none',
    maxWidth: '320px',
    padding: '6px 8px',
    borderRadius: '6px',
    background: 'rgba(0, 0, 0, 0.85)',
    color: '#ffffff',
    fontFamily: "'Open Sans', sans-serif",
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '18px',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.16)',
    overflowWrap: 'anywhere',
    whiteSpace: 'normal',
    visibility: 'hidden',
    left: '0px',
    top: '0px',
};

/** The subset of an `@antv/g` display object this module reads. */
interface AxisLabelShapeLike {
    nodeName?: string;
    className?: string;
    parentNode?: AxisLabelShapeLike | null;
    parentElement?: AxisLabelShapeLike | null;
    attr?: (name: string) => unknown;
    style?: Record<string, unknown>;
    children?: ArrayLike<AxisLabelShapeLike>;
}

/** The subset of the G2 chart / G canvas API this module reads. */
interface AxisLabelTooltipTarget {
    getContext?: () => { canvas?: { document?: { documentElement?: unknown } } } | undefined;
}

interface GEventTargetLike {
    addEventListener: (type: string, listener: (event: unknown) => void) => void;
    removeEventListener: (type: string, listener: (event: unknown) => void) => void;
}

interface GPointerEventLike {
    target?: AxisLabelShapeLike | null;
    client?: { x?: number; y?: number };
    nativeEvent?: { clientX?: number; clientY?: number };
}

export interface AxisLabelHoverTooltipOptions {
    /** Font size the tick labels render at; used to decide whether a label is being truncated. */
    fontSizePx?: number;
    /** Owner document the tooltip element is created in (tests inject one). */
    ownerDocument?: Document;
}

function hasAxisLabelClassName(shape: AxisLabelShapeLike): boolean {
    return typeof shape.className === 'string' && shape.className.includes(AXIS_LABEL_ITEM_CLASS_NAME);
}

/**
 * Walk up from an event target to the axis tick label shape that contains it, if any.
 *
 * The label item is usually the text shape itself, but a formatter may return a group, in which case
 * the event fires on a descendant.
 */
export function findAxisLabelShape(
    target: AxisLabelShapeLike | null | undefined,
    maxDepth = 8,
): AxisLabelShapeLike | undefined {
    let current = target ?? undefined;
    for (let depth = 0; current && depth <= maxDepth; depth++) {
        if (hasAxisLabelClassName(current)) return current;
        current = (current.parentNode ?? current.parentElement) || undefined;
    }
    return undefined;
}

/**
 * The text node the wrap / ellipsis styles were applied to: the label shape itself when it is a text
 * node, otherwise its single text child. Mirrors `Un` in `@antv/component`, widened to search deeper
 * so a formatter returning a nested group still resolves.
 */
export function resolveAxisLabelTextNode(
    shape: AxisLabelShapeLike | null | undefined,
    maxDepth = 4,
): AxisLabelShapeLike | undefined {
    if (!shape) return undefined;
    if (shape.nodeName === 'text') return shape;
    if (maxDepth <= 0) return undefined;

    const children = shape.children;
    for (let i = 0; i < (children?.length ?? 0); i++) {
        const resolved = resolveAxisLabelTextNode(children![i], maxDepth - 1);
        if (resolved) return resolved;
    }
    /** No text node in the subtree: fall back to the shape, whose own style bag may still carry text. */
    return shape;
}

function readShapeStyle(shape: AxisLabelShapeLike | null | undefined, name: string): unknown {
    if (!shape) return undefined;
    try {
        const fromAttr = shape.attr?.(name);
        if (fromAttr !== undefined && fromAttr !== null) return fromAttr;
    } catch {
        // `attr` throws on destroyed shapes mid-relayout; fall through to the style bag.
    }
    return shape.style?.[name];
}

/** Read a style from the label shape, falling back to the text node the transforms wrote it on. */
function readAxisLabelStyle(shape: AxisLabelShapeLike | null | undefined, name: string): unknown {
    const own = readShapeStyle(shape, name);
    if (own !== undefined && own !== null) return own;
    const textNode = resolveAxisLabelTextNode(shape);
    return textNode === shape ? undefined : readShapeStyle(textNode, name);
}

/** Read the untruncated label text off a tick label shape. */
export function readAxisLabelText(shape: AxisLabelShapeLike | null | undefined): string {
    const text = readAxisLabelStyle(shape, 'text');
    if (typeof text === 'string') return text;
    if (typeof text === 'number') return String(text);
    return '';
}

/**
 * Whether hovering `shape` should reveal its full text.
 *
 * True only when the axis is wrapping the label (`wordWrapWidth` is set by the `wrap` transform) and
 * the text does not fit on a single line — i.e. it is either broken across lines or ellipsised.
 * Deliberately inclusive: a wrapped-but-complete label showing its own name on hover is harmless,
 * whereas an ellipsised label with no tooltip is the failure this exists to prevent.
 */
export function shouldShowAxisLabelTooltip(
    shape: AxisLabelShapeLike | null | undefined,
    fontSizePx: number = AXIS_LABEL_FONT_SIZE_PX,
): boolean {
    const text = readAxisLabelText(shape);
    if (!text.trim()) return false;

    const wrapWidth = Number(readAxisLabelStyle(shape, 'wordWrapWidth'));
    if (!Number.isFinite(wrapWidth) || wrapWidth <= 0) return false;

    if (text.includes('\n')) return true;
    return estimateTextWidthPx(text, fontSizePx) > wrapWidth;
}

/** Collapse the newlines a legacy formatter may have inserted so the tooltip reads as one name. */
export function normalizeAxisLabelTooltipText(text: string): string {
    return text.replace(/\s+/g, ' ').trim();
}

function pointerClientPosition(event: GPointerEventLike | undefined): { x: number; y: number } {
    return {
        x: event?.nativeEvent?.clientX ?? event?.client?.x ?? 0,
        y: event?.nativeEvent?.clientY ?? event?.client?.y ?? 0,
    };
}

function createTooltipElement(ownerDocument: Document): HTMLDivElement {
    const el = ownerDocument.createElement('div');
    el.setAttribute(AXIS_LABEL_TOOLTIP_MARKER, '');
    el.setAttribute('role', 'tooltip');
    /** Excluded from chart image export, which snapshots the card, not the pointer state. */
    el.setAttribute('data-export-exclude', '');
    Object.assign(el.style, TOOLTIP_STYLE);
    ownerDocument.body.appendChild(el);
    return el;
}

const noop = () => undefined;

/**
 * Show the full tick label text while the pointer is over a wrapped / ellipsised axis label.
 *
 * Call once per chart instance, after `render()` resolves, and invoke the returned function on
 * teardown (it removes the listeners and the tooltip element).
 */
export function attachAxisLabelHoverTooltip(
    chart: AxisLabelTooltipTarget | null | undefined,
    options: AxisLabelHoverTooltipOptions = {},
): () => void {
    const { fontSizePx = AXIS_LABEL_FONT_SIZE_PX, ownerDocument } = options;

    const root = chart?.getContext?.()?.canvas?.document?.documentElement as GEventTargetLike | undefined;
    if (!root || typeof root.addEventListener !== 'function') return noop;

    const doc = ownerDocument ?? (typeof document !== 'undefined' ? document : undefined);
    if (!doc?.body) return noop;

    let tooltip: HTMLDivElement | null = null;

    const hide = () => {
        if (tooltip) tooltip.style.visibility = 'hidden';
    };

    const show = (text: string, clientX: number, clientY: number) => {
        if (!tooltip) tooltip = createTooltipElement(doc);
        tooltip.textContent = text;
        tooltip.style.visibility = 'visible';

        const viewportWidth = doc.documentElement?.clientWidth || 0;
        const viewportHeight = doc.documentElement?.clientHeight || 0;
        const { width, height } = tooltip.getBoundingClientRect?.() ?? { width: 0, height: 0 };

        let left = clientX + TOOLTIP_POINTER_OFFSET_PX;
        let top = clientY + TOOLTIP_POINTER_OFFSET_PX;
        if (viewportWidth && left + width + TOOLTIP_VIEWPORT_MARGIN_PX > viewportWidth) {
            left = Math.max(TOOLTIP_VIEWPORT_MARGIN_PX, clientX - TOOLTIP_POINTER_OFFSET_PX - width);
        }
        if (viewportHeight && top + height + TOOLTIP_VIEWPORT_MARGIN_PX > viewportHeight) {
            top = Math.max(TOOLTIP_VIEWPORT_MARGIN_PX, clientY - TOOLTIP_POINTER_OFFSET_PX - height);
        }

        tooltip.style.left = `${Math.round(left)}px`;
        tooltip.style.top = `${Math.round(top)}px`;
    };

    const onPointerMove = (rawEvent: unknown) => {
        const event = (rawEvent ?? {}) as GPointerEventLike;
        const shape = findAxisLabelShape(event.target);
        if (!shape || !shouldShowAxisLabelTooltip(shape, fontSizePx)) {
            hide();
            return;
        }
        const text = normalizeAxisLabelTooltipText(readAxisLabelText(shape));
        if (!text) {
            hide();
            return;
        }
        const { x, y } = pointerClientPosition(event);
        show(text, x, y);
    };

    const onPointerLeave = () => hide();

    root.addEventListener('pointermove', onPointerMove);
    root.addEventListener('pointerleave', onPointerLeave);
    root.addEventListener('pointerout', onPointerLeave);
    /** Charts pan on wheel/scroll, which moves labels out from under a pinned tooltip. */
    const windowRef = typeof window !== 'undefined' ? window : undefined;
    windowRef?.addEventListener('wheel', onPointerLeave, { passive: true });
    windowRef?.addEventListener('scroll', onPointerLeave, { passive: true, capture: true });

    return () => {
        root.removeEventListener('pointermove', onPointerMove);
        root.removeEventListener('pointerleave', onPointerLeave);
        root.removeEventListener('pointerout', onPointerLeave);
        windowRef?.removeEventListener('wheel', onPointerLeave);
        windowRef?.removeEventListener('scroll', onPointerLeave, { capture: true });
        tooltip?.parentNode?.removeChild(tooltip);
        tooltip = null;
    };
}
